import {
  makeStyles,
  shorthands,
  tokens,
  type GriffelStyle,
} from "@fluentui/react-components";
import type { Appearance } from "../data";

type Styles = {
  root: GriffelStyle;
  dropdown: GriffelStyle;
  truncatedText: GriffelStyle;
  placeholderText: GriffelStyle;
  optionContainer: GriffelStyle;
  optionBadge: GriffelStyle;
  filterListBoxOverflowFrom10Items: GriffelStyle;
  inactiveFilterButton: GriffelStyle;
  activeFilterButton: GriffelStyle;
  inactiveFilterButtonSelected: GriffelStyle;
  activeFilterButtonSelected: GriffelStyle;
  disabledButton: GriffelStyle;
  activeFilterLabel: GriffelStyle;
  activeFilterLabelSelected: GriffelStyle;
  inactiveFilterLabel: GriffelStyle;
  inactiveFilterLabelSelected: GriffelStyle;
  activeDisplayedValueText: GriffelStyle;
  inactiveDisplayedValueText: GriffelStyle;
  activeDisplayedValueTextSelected: GriffelStyle;
  inactiveDisplayedValueTextSelected: GriffelStyle;
  disabledButtonFilterLabel: GriffelStyle;
  disabledButtonDisplayedValueText: GriffelStyle;
};

const defaultStyles: Styles = {
  root: {
    display: "flex",
    alignItems: "center",
    alignSelf: "stretch",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    ...shorthands.gap("1rem"),
    zIndex: 1, // Add z-index style so dropdown doesn't get hidden behind other elements
  },

  filterListBoxOverflowFrom10Items: {
    maxHeight: "380px !important",
  },

  optionContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    flexDirection: "row",
    overflowWrap: "anywhere",
  },

  optionBadge: {
    marginRight: "6px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "20px",
    flexShrink: 0,
  },

  dropdown: {
    minWidth: "max-content",
    maxWidth: "240px",
    flex: 1,
    width: "20vw",
    "@media (max-width: 480px)": {
      maxWidth: "100%",
    },
  },

  truncatedText: {
    overflowX: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  placeholderText: {
    color: tokens.colorNeutralForeground4,
  },

  // Styles for inactive filter states(Rest,hover,clicked) when the default option is select all
  inactiveFilterButton: {
    backgroundColor: tokens.colorSubtleBackground,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: "9999px",
    "&:hover": {
      backgroundColor: tokens.colorSubtleBackgroundHover,
      border: `1px solid ${tokens.colorNeutralStroke1Hover}`,
      borderRadius: "9999px",
    },
    "&:active": {
      backgroundColor: `${tokens.colorSubtleBackgroundPressed} !important`,
      border: `1px solid ${tokens.colorNeutralStroke1Pressed} !important`,
      borderRadius: "9999px !important",
    },
  },

  // Styles for active filter states(Rest,hover,clicked) when the default option is other than select all
  activeFilterButton: {
    backgroundColor: tokens.colorBrandBackground2,
    border: `2px solid ${tokens.colorBrandStroke2}`,
    borderRadius: "var(--button-corner-radius, 9999px)",
    "&:hover": {
      backgroundColor: tokens.colorBrandBackground2Hover,
      border: `2px solid ${tokens.colorBrandStroke2Hover}`,
      borderRadius: "var(--button-corner-radius, 9999px)",
    },
    "&:active": {
      backgroundColor: `${tokens.colorBrandBackground2Pressed} !important`,
      border: `2px solid ${tokens.colorBrandStroke2Pressed} !important`,
      borderRadius: "var(--button-corner-radius, 9999px) !important",
    },
  },

  // Styles when the inactive filter is opened
  inactiveFilterButtonSelected: {
    backgroundColor: `${tokens.colorSubtleBackgroundSelected} !important`,
    border: `2px solid ${tokens.colorNeutralStroke1Selected}`,
    borderRadius: "9999px",
  },

  // Styles when the active filter is opened
  activeFilterButtonSelected: {
    backgroundColor: `${tokens.colorBrandBackground} !important`,
    border: `2px solid ${tokens.colorBrandStroke1} !important`,
    borderRadius: "var(--button-corner-radius, 9999px) !important",
  },

  // Styles for disabled filter
  disabledButton: {
    backgroundColor: `${tokens.colorTransparentStrokeDisabled} !important`,
    border: `1px solid ${tokens.colorNeutralStrokeDisabled} !important`,
    borderRadius: "var(--button-corner-radius, 9999px) !important",
  },

  disabledButtonFilterLabel: {
    color: tokens.colorNeutralForegroundDisabled,
    fontFamily: "Segoe UI",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "20px",
  },

  disabledButtonDisplayedValueText: {
    color: tokens.colorNeutralForegroundDisabled,
    fontFamily: "Segoe UI",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "20px",
  },

  // Styles for active and inactive filter labels
  inactiveFilterLabel: {
    color: tokens.colorNeutralForeground1,
    fontFamily: "Segoe UI",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "20px",
  },

  inactiveFilterLabelSelected: {
    color: tokens.colorNeutralForeground1Selected,
    fontFamily: "Segoe UI",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "20px",
  },

  activeFilterLabel: {
    color: tokens.colorNeutralForeground1,
    fontFamily: "Segoe UI",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "20px",
  },

  activeFilterLabelSelected: {
    color: tokens.colorNeutralForegroundInverted,
    fontFamily: "Segoe UI",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "20px",
  },

  // Styles for active and inactive displayed value text in the filter
  activeDisplayedValueText: {
    color: tokens.colorNeutralForeground1,
    fontFamily: "Segoe UI",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "20px",
  },

  activeDisplayedValueTextSelected: {
    color: tokens.colorNeutralForegroundInverted,
    fontFamily: "Segoe UI",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "20px",
  },

  inactiveDisplayedValueText: {
    color: tokens.colorNeutralForeground1,
    fontFamily: "Segoe UI",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "20px",
  },

  inactiveDisplayedValueTextSelected: {
    color: tokens.colorNeutralForeground1Selected,
    fontFamily: "Segoe UI",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "20px",
  },
};

const singleColumnStyles: Styles = {
  ...defaultStyles,
  dropdown: {
    ...defaultStyles.dropdown,

    "@media (max-width: 480px)": {
      maxWidth: "148px",
    },

    "& button": {
      color: tokens.colorNeutralForeground1,
      fontSize: tokens.fontSizeBase300,
      fontStyle: "normal",
      fontWeight: tokens.fontWeightSemibold,
      lineHeight: "20px",
    },
  },
};

export const useInAppStyles = makeStyles(defaultStyles);
export const useCopilotLabStyles = makeStyles(defaultStyles);
export const useM365Styles = makeStyles(defaultStyles);
export const useSingleColumnStyle = makeStyles(singleColumnStyles);

export const useStyles = (appearance?: Appearance) => {
  switch (appearance) {
    case "in-app":
      return useInAppStyles;
    case "web":
    case "web-v3":
      return useCopilotLabStyles;
    case "m365":
    case "bing-web":
    case "win-copilot":
      return useM365Styles;
    case "bing-mobile":
    case "underside":
    case "single-col":
      return useSingleColumnStyle;
    default:
      return useInAppStyles;
  }
};
